/* global GPUBufferUsage, GPUTextureUsage */
// The physical paint engine on a model's UV atlas (3D surface adapter).
//
// Same GPU engine, brushes, media, pigments and physics as the 2D Paint Tool
// (PhysicalPaintSystem); what differs is only how strokes reach the grid and
// how the result is shown:
//   strokes   pointer hits on the model -> SurfaceStroke (tangent plane,
//             seams) -> engine.stampMapper -> island-masked GPU stamps
//   surface   the model material's own surface preset (not the 2D paper);
//             cells outside every UV island are an inert sink
//   display   unlit paint colour (premultiplied, coverage in alpha) and paint
//             height, rendered on the GPU into two bitmaps with a seam gutter;
//             the 3D viewport applies them to its materials and lights them
//             with its own lights (raised paint through bump mapping)
//   undo      GPU snapshots before each stroke, redo copies on undo
//   saving    sparse physical state (LayerStateCodec), encoded in a worker
// Nothing is simulated on the CPU while painting.

import { createGpuPaintEngine } from '../gpu/WebGPUPaintEngine.js';
import { createSurface, physicalSurfaceOf } from '../surfaces/PaperModel.js';
import { SURFACE_LIBRARY } from '../surfaces/SurfaceLibrary.js';
import { paperSpectrum } from '../pigments/PigmentLibrary.js';
import { decodeLayerState, LAYER_STATE_VERSION } from '../integration/LayerStateCodec.js';
import { environmentFromPaintSettings, paintSampleFromPoint, toolFromPaintSettings } from '../integration/PhysicalPaintSystem.js';
import { createSurfaceStroke } from './SurfaceStroke.js';

// Surfaces offered for model materials (subset of the paper library).
export const MODEL_SURFACE_PRESETS = Object.freeze([
  { id: 'smooth_panel', label: 'Smooth primed surface' },
  { id: 'gesso_canvas', label: 'Gesso' },
  { id: 'canvas_fine', label: 'Linen canvas' },
  { id: 'rough', label: 'Rough paper' },
  { id: 'wood_panel', label: 'Wood' },
  { id: 'custom', label: 'Custom material' },
]);
export const DEFAULT_MODEL_SURFACE = 'smooth_panel';
// Simulation cells for a model (each undo level is a full GPU copy, ~250 bytes per cell).
export const MAX_MODEL_SIM_CELLS = 640 * 640;
export const MODEL_SNAPSHOT_BUDGET_BYTES = 768 * 1024 * 1024;
// Height output: paint elevation mapped to 0..1 over this many cells of height.
export const HEIGHT_RANGE = 3;
const SEED = 7;

export function modelSimSize(atlasWidth, atlasHeight, maxCells = MAX_MODEL_SIM_CELLS) {
  const w = Math.max(8, Math.round(atlasWidth / 2));
  const h = Math.max(8, Math.round(atlasHeight / 2));
  const f = w * h > maxCells ? Math.sqrt(maxCells / (w * h)) : 1;
  return { width: Math.max(8, Math.round(w * f)), height: Math.max(8, Math.round(h * f)) };
}

// Surface fields for the model grid; cells outside every island absorb water
// and never pass it on (they are not part of the model).
export function modelSurfaceFields(map, presetId) {
  const preset = SURFACE_LIBRARY[presetId] ? presetId : DEFAULT_MODEL_SURFACE;
  const base = physicalSurfaceOf(createSurface(map.width, map.height, preset, SEED, {}), 'physical', 1);
  const n = map.width * map.height;
  const heightField = Float32Array.from(base.heightField);
  const absorbency = Float32Array.from(base.absorbency);
  const capillary = Float32Array.from(base.capillary);
  const pinning = Float32Array.from(base.pinning);
  for (let i = 0; i < n; i += 1) {
    if (map.island[i]) continue;
    heightField[i] = 0.5;
    absorbency[i] = 40;
    capillary[i] = 0;
    pinning[i] = 1;
  }
  return { ...base, heightField, absorbency, capillary, pinning };
}

const GUTTER_WGSL = /* wgsl */ `
@group(0) @binding(0) var src: texture_2d<f32>;
@group(0) @binding(1) var dst: texture_storage_2d<rgba8unorm, write>;
@group(0) @binding(2) var<storage, read> source: array<i32>;
@group(0) @binding(3) var<uniform> dims: vec4<u32>;
@compute @workgroup_size(8, 8)
fn main(@builtin(global_invocation_id) g: vec3<u32>) {
  if (g.x >= dims.x || g.y >= dims.y) { return; }
  let s = source[g.y * dims.x + g.x];
  var c = vec4<f32>(0.0, 0.0, 0.0, 0.0);
  if (s >= 0) { c = textureLoad(src, vec2<i32>(s % i32(dims.x), s / i32(dims.x)), 0); }
  textureStore(dst, vec2<i32>(g.xy), c);
}`;

export function createSurfacePaintTarget(gpu, map, options = {}) {
  const { device } = gpu;
  const W = map.width;
  const H = map.height;
  let surfacePreset = SURFACE_LIBRARY[options.surface] ? options.surface : DEFAULT_MODEL_SURFACE;
  let surfacePhysics = modelSurfaceFields(map, surfacePreset);
  const substrateOf = () => paperSpectrum(surfacePhysics.preset.tone ?? 0.9, surfacePhysics.preset.warmth ?? 0.02);
  let substrate = substrateOf();
  const engine = createGpuPaintEngine(gpu, { width: W, height: H, surfaceData: surfacePhysics, substrate, seed: SEED, cellIslands: map.island });
  engine.scheduler.maxStepsPerFrame = 4;
  // Only the paint is raised in the height output (the model has its own geometry).
  engine.setDisplaySurface(surfacePhysics.heightField, { relief: 0 });
  engine.setSubstrate(substrate);

  let destroyed = false;
  let stroke = null; // { surface: SurfaceStroke, input, settings, begun }
  let wet = false;
  let dirty = true;
  let containsPaint = false;
  let checking = false;
  let lastActivityCheck = 0;
  const undo = [];
  const redo = [];
  let snapshotBytes = 0;
  const stats = { strokes: 0, lastStrokeStamps: 0, lastMappedStamps: 0, lastSeamSamples: 0, renderMs: 0, renders: 0 };

  // ---------------------------------------------------------------- display (GPU)
  const makeCanvas = () => {
    const canvas = new OffscreenCanvas(W, H);
    const context = canvas.getContext('webgpu');
    context.configure({ device, format: 'rgba8unorm', alphaMode: 'premultiplied', usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.RENDER_ATTACHMENT });
    return { canvas, context };
  };
  const colourOut = makeCanvas();
  const heightOut = makeCanvas();
  const scratch = device.createTexture({ size: [W, H], format: 'rgba8unorm', usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING });
  const sourceBuffer = device.createBuffer({ size: W * H * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
  device.queue.writeBuffer(sourceBuffer, 0, map.displaySource);
  const dimsBuffer = device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
  device.queue.writeBuffer(dimsBuffer, 0, new Uint32Array([W, H, 0, 0]));
  const gutterPipe = device.createComputePipeline({ layout: 'auto', compute: { module: device.createShaderModule({ label: 'paint3d.gutter', code: GUTTER_WGSL }), entryPoint: 'main' } });

  const gutterInto = (out) => {
    const target = out.context.getCurrentTexture();
    const group = device.createBindGroup({
      layout: gutterPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: scratch.createView() },
        { binding: 1, resource: target.createView() },
        { binding: 2, resource: { buffer: sourceBuffer } },
        { binding: 3, resource: { buffer: dimsBuffer } },
      ],
    });
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(gutterPipe);
    pass.setBindGroup(0, group);
    pass.dispatchWorkgroups(Math.ceil(W / 8), Math.ceil(H / 8));
    pass.end();
    device.queue.submit([encoder.finish()]);
    return out.canvas.transferToImageBitmap();
  };

  // Paint colour (sRGB, premultiplied, alpha = coverage) and paint height maps.
  const renderMaps = () => {
    const t0 = performance.now();
    engine.renderToView(scratch.createView(), 'rgba8unorm', { view: 'pigment' }, 1);
    const colour = gutterInto(colourOut);
    engine.renderToView(scratch.createView(), 'rgba8unorm', { view: 'height', paperRelief: 0, maxElevation: HEIGHT_RANGE, depthScale: 1 }, 0);
    const height = gutterInto(heightOut);
    dirty = false;
    stats.renders += 1;
    stats.renderMs = performance.now() - t0;
    return { colour, height };
  };

  // ---------------------------------------------------------------- strokes
  const sampleFor = (planePoint, hit, time) => paintSampleFromPoint({
    x: planePoint.x,
    y: planePoint.y,
    pressure: hit.pressure,
    inputPressure: hit.pressure,
    pointerType: hit.pointerType,
    tiltX: hit.tiltX,
    tiltY: hit.tiltY,
    twist: hit.twist,
  }, stroke.settings, stroke.input, 1, time);

  const pushUndoSnapshot = () => {
    const gpuCopy = engine.captureSnapshot();
    const entry = { gpu: gpuCopy, bytes: gpuCopy.bytes || engine.snapshotBytes(), containsPaint, evicted: false };
    snapshotBytes += entry.bytes;
    undo.push(entry);
    redo.splice(0).forEach(releaseSnapshot);
    while (snapshotBytes > MODEL_SNAPSHOT_BUDGET_BYTES && undo.length > 1) {
      const oldest = undo.find((e) => !e.evicted);
      if (!oldest) break;
      releaseSnapshot(oldest);
    }
  };
  function releaseSnapshot(entry) {
    if (!entry || entry.evicted) return;
    engine.destroySnapshot(entry.gpu);
    snapshotBytes -= entry.bytes;
    entry.gpu = null;
    entry.evicted = true;
  }

  // settings: the 2D Paint Tool settings (normalised); brushWorldRadius: model units.
  function beginStroke({ settings, hit, brushWorldRadius, strokeId = '', paintableMaterial = -1 }) {
    if (destroyed) return false;
    if (stroke) endStroke();
    const radiusCells = Math.max(0.75, brushWorldRadius / map.texelWorld);
    const surface = createSurfaceStroke(map, { radiusCells, allowedMaterial: paintableMaterial });
    const toolSize = Math.max(1, Number(settings.toolSize) || 70);
    const tool = toolFromPaintSettings(settings, radiusCells / (toolSize / 2), strokeId || `paint3d-${Date.now()}`);
    stroke = { surface, settings, input: { travelled: 0, last: null, pointerType: '' }, begun: false, tool, startedAt: performance.now() };
    engine.stampMapper = (stamp, push) => surface.mapStamp(stamp, push);
    engine.bladeMapper = (step, push) => surface.mapBlade(step, push);
    const planePoint = surface.start(hit);
    if (!planePoint) return true; // waiting for a hit with UVs
    begin(planePoint, hit);
    return true;
  }

  function begin(planePoint, hit) {
    pushUndoSnapshot();
    engine.setEnvironment(environmentFromPaintSettings(stroke.settings));
    engine.beginStroke(stroke.tool, sampleFor(planePoint, hit, hit.time ?? performance.now()));
    stroke.begun = true;
    containsPaint = true;
    wet = true;
    dirty = true;
  }

  function strokeTo(hit) {
    if (!stroke || destroyed) return false;
    const planePoint = stroke.begun ? stroke.surface.move(hit) : stroke.surface.start(hit);
    if (!planePoint) return false;
    if (!stroke.begun) { begin(planePoint, hit); return true; }
    engine.strokeTo(sampleFor(planePoint, hit, hit.time ?? performance.now()));
    dirty = true;
    return true;
  }

  // Returns true when the stroke put paint down (one undo step).
  function endStroke() {
    if (!stroke) return false;
    const done = stroke;
    stroke = null;
    engine.stampMapper = null;
    engine.bladeMapper = null;
    if (!done.begun) return false;
    engine.endStroke();
    stats.strokes += 1;
    stats.lastStrokeStamps = done.surface.stats.stamps;
    stats.lastMappedStamps = done.surface.stats.mappedStamps;
    stats.lastSeamSamples = done.surface.stats.seamSamples;
    stats.lastStrokeSamples = done.surface.stats.samples;
    stats.lastMissedSamples = done.surface.stats.missed;
    dirty = true;
    return true;
  }

  // ---------------------------------------------------------------- simulation clock
  // Called every viewport frame; returns true when the maps should be re-rendered.
  function advance(dtSeconds, now = performance.now()) {
    if (destroyed) return false;
    if (wet) {
      engine.advance(Math.min(0.25, Math.max(0, dtSeconds)));
      dirty = true;
      if (!stroke && now - lastActivityCheck > 500 && !checking) {
        checking = true;
        lastActivityCheck = now;
        engine.readCounters().then((c) => {
          checking = false;
          if (!c.regionTiles && !stroke) wet = false;
        }).catch(() => { checking = false; });
      }
    }
    return dirty;
  }

  // ---------------------------------------------------------------- undo / redo
  // Moves to the state after `count` strokes (count = strokes kept).
  function strokeCount() { return undo.length; }
  function setStrokeCount(count) {
    const target = Math.max(0, Math.floor(Number(count) || 0));
    let changed = false;
    while (undo.length > target) {
      const entry = undo[undo.length - 1];
      if (entry.evicted) break; // older than the undo budget
      const current = engine.captureSnapshot();
      redo.push({ gpu: current, bytes: current.bytes || engine.snapshotBytes(), containsPaint, evicted: false });
      snapshotBytes += redo[redo.length - 1].bytes;
      undo.pop();
      engine.restoreSnapshot(entry.gpu);
      containsPaint = entry.containsPaint;
      releaseSnapshot(entry);
      changed = true;
    }
    while (undo.length < target && redo.length) {
      const entry = redo.pop();
      const current = engine.captureSnapshot();
      undo.push({ gpu: current, bytes: current.bytes || engine.snapshotBytes(), containsPaint, evicted: false });
      snapshotBytes += undo[undo.length - 1].bytes;
      engine.restoreSnapshot(entry.gpu);
      containsPaint = entry.containsPaint;
      releaseSnapshot(entry);
      changed = true;
    }
    if (changed) { wet = true; dirty = true; }
    return undo.length;
  }

  // ---------------------------------------------------------------- persistence
  let encodeWorker = null;
  let encodeSeq = 0;
  const pending = new Map();
  async function exportState() {
    if (!containsPaint) return null;
    const rb = await engine.readback();
    const S = rb.fields.water.buffer;
    const P = rb.wetPig[0].buffer;
    const R = rb.dryRefl.buffer;
    const meta = {
      width: W,
      height: H,
      slots: engine.state.slots.map((p) => (p ? { id: p.id, code: p.code, target: p.target } : null)),
      substrate: Array.from(engine.substrate()),
      surface: { preset: surfacePreset },
      docWidth: W,
      docHeight: H,
    };
    if (!encodeWorker) {
      const { createLayerStateWorker } = await import('../integration/layerStateWorkerClient.js');
      encodeWorker = createLayerStateWorker();
      encodeWorker.onmessage = (event) => {
        const cb = pending.get(event.data.id);
        pending.delete(event.data.id);
        if (!cb) return;
        if (event.data.error) cb.reject(new Error(event.data.error));
        else cb.resolve(event.data.state);
      };
    }
    const id = ++encodeSeq;
    const state = await new Promise((resolve, reject) => {
      pending.set(id, { resolve, reject });
      encodeWorker.postMessage({ id, S: new Float32Array(S), P: new Float32Array(P), R: new Float32Array(R), n: W * H, meta }, [S, P, R]);
    });
    return { ...state, modelSurface: surfacePreset };
  }

  // Restores a saved state of the same grid size (another UV layout is refused).
  function importState(state) {
    if (!state || state.version !== LAYER_STATE_VERSION) return false;
    if (state.simWidth !== W || state.simHeight !== H) return false;
    if (state.modelSurface && state.modelSurface !== surfacePreset) setSurface(state.modelSurface);
    const cpu = decodeLayerState(state, surfacePhysics, substrate);
    engine.upload(cpu);
    containsPaint = true;
    wet = true;
    dirty = true;
    return true;
  }

  // ---------------------------------------------------------------- material surface
  function setSurface(presetId) {
    if (!SURFACE_LIBRARY[presetId] || presetId === surfacePreset) return false;
    surfacePreset = presetId;
    surfacePhysics = modelSurfaceFields(map, surfacePreset);
    engine.setPhysicsSurface(surfacePhysics);
    engine.setDisplaySurface(surfacePhysics.heightField, { relief: 0 });
    // Existing paint keeps its substrate; new paint dries over the new surface colour.
    substrate = substrateOf();
    engine.setSubstrate(substrate);
    dirty = true;
    return true;
  }

  function dryAll() {
    engine.dryAll();
    dirty = true;
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    undo.concat(redo).forEach(releaseSnapshot);
    engine.destroy();
    scratch.destroy();
    sourceBuffer.destroy();
    dimsBuffer.destroy();
    if (encodeWorker) encodeWorker.terminate();
  }

  return {
    width: W,
    height: H,
    map,
    engine,
    stats,
    beginStroke,
    strokeTo,
    endStroke,
    isStroking: () => Boolean(stroke),
    advance,
    renderMaps,
    isWet: () => wet,
    hasPaint: () => containsPaint,
    strokeCount,
    setStrokeCount,
    exportState,
    importState,
    getSurface: () => surfacePreset,
    setSurface,
    dryAll,
    gpuBytes: () => engine.snapshotBytes() + snapshotBytes,
    destroy,
  };
}
